export const initialScenario = {
    formatVersion: 7,
    title: 'Chest pain triage',
    startNodeId: 'start',
    // V6 demo chrome: a branded title screen and conclusion so the canonical
    // example exercises the full intro → play → conclusion → exit flow. The
    // background is a deep clinical navy; the runtime auto-picks white foreground
    // for AA contrast. No logoUrl (no bundled asset) — the editor's upload wires
    // that in a later slice.
    branding: {
        backgroundColor: '#15233B',
    },
    intro: {
        heading: 'Chest pain triage',
        body: 'A patient has arrived with chest pain. Work through the decisions a clinician faces in the first few minutes. Your choices change what happens next.',
        buttonLabel: 'Begin scenario',
    },
    closing: {
        heading: 'Scenario complete',
        body: 'Thanks for working through this triage scenario. You can start over to explore a different path, or exit.',
        buttonLabel: 'Exit',
    },
    variables: {},
    objectives: {
        obj_safety: {
            code: 'LO-1',
            description: 'Recognize red-flag symptoms and act on them in a timely way',
            bloomLevel: 'evaluate',
        },
        obj_communication: {
            code: 'LO-2',
            description: 'Maintain patient trust through clear, empathetic communication',
            bloomLevel: 'apply',
        },
    },
    nodes: {
        start: {
            id: 'start', title: 'Patient arrives', type: 'start', x: 360, y: 40,
            objectiveTags: ['obj_safety', 'obj_communication'],
            variants: [{
                    id: 'v_start_0', condition: null,
                    sceneText: 'A 45-year-old walks into your clinic complaining of intermittent chest pain over the past week. They look anxious. How do you respond?',
                    media: [],
                }],
            choices: [
                { id: 'c_start_1', label: 'Reassure them — probably nothing serious',
                    destinations: [{ condition: null, destinationId: 'frustrated' }],
                    feedbackVariants: [{ id: 'vf_start_1', condition: null, text: '' }] },
                { id: 'c_start_2', label: 'Ask detailed questions about the pain',
                    destinations: [{ condition: null, destinationId: 'symptoms' }],
                    feedbackVariants: [{ id: 'vf_start_2', condition: null, text: '' }],
                    objectiveTags: ['obj_safety', 'obj_communication'] },
                { id: 'c_start_3', label: 'Escalate to a senior colleague',
                    destinations: [{ condition: null, destinationId: 'senior' }],
                    feedbackVariants: [{ id: 'vf_start_3', condition: null, text: '' }],
                    objectiveTags: ['obj_safety'] },
            ],
        },
        frustrated: {
            id: 'frustrated', title: 'Patient frustrated', type: 'decision', x: 60, y: 240,
            variants: [{
                    id: 'v_frustrated_0', condition: null,
                    sceneText: '"But it really hurts when I climb stairs," they say. They look unconvinced and you can feel the trust slipping away.',
                    media: [],
                }],
            choices: [
                { id: 'c_frustrated_1', label: 'Apologize and ask the questions you skipped',
                    destinations: [{ condition: null, destinationId: 'decision' }],
                    feedbackVariants: [{ id: 'vf_frustrated_1', condition: null, text: '' }] },
                { id: 'c_frustrated_2', label: 'Refer them to another GP',
                    destinations: [{ condition: null, destinationId: 'mixed' }],
                    feedbackVariants: [{ id: 'vf_frustrated_2', condition: null, text: '' }] },
            ],
        },
        symptoms: {
            id: 'symptoms', title: 'Symptoms revealed', type: 'decision', x: 360, y: 240,
            objectiveTags: ['obj_safety'],
            variants: [{
                    id: 'v_symptoms_0', condition: null,
                    sceneText: 'The patient describes pain on exertion, relieved by rest, sometimes radiating to the left arm. This sounds like possible angina.',
                    media: [],
                }],
            choices: [
                { id: 'c_symptoms_1', label: 'Order an ECG and bloods now',
                    destinations: [{ condition: null, destinationId: 'good' }],
                    feedbackVariants: [{ id: 'vf_symptoms_1', condition: null, text: 'Good call — early objective evidence is exactly what this needs.' }],
                    objectiveTags: ['obj_safety'] },
                { id: 'c_symptoms_2', label: 'Refer to cardiology and send home',
                    destinations: [{ condition: null, destinationId: 'decision' }],
                    feedbackVariants: [{ id: 'vf_symptoms_2', condition: null, text: '' }] },
            ],
        },
        senior: {
            id: 'senior', title: 'Senior arrives', type: 'decision', x: 660, y: 240,
            variants: [{
                    id: 'v_senior_0', condition: null,
                    sceneText: "Your consultant arrives within 10 minutes. They appreciate the cautious instinct but ask why you couldn't do the initial assessment yourself.",
                    media: [],
                }],
            choices: [
                { id: 'c_senior_1', label: "Brief them on what you've already observed",
                    destinations: [{ condition: null, destinationId: 'handover' }],
                    feedbackVariants: [{ id: 'vf_senior_1', condition: null, text: '' }] },
                { id: 'c_senior_2', label: "Admit you weren't confident with the symptoms",
                    destinations: [{ condition: null, destinationId: 'handover' }],
                    feedbackVariants: [{ id: 'vf_senior_2', condition: null, text: '' }] },
            ],
        },
        decision: {
            id: 'decision', title: 'Decision point', type: 'decision', x: 200, y: 460,
            variants: [{
                    id: 'v_decision_0', condition: null,
                    sceneText: 'You have to commit to a clinical pathway. Investigate now, or send home with safety-netting?',
                    media: [],
                }],
            choices: [
                { id: 'c_decision_1', label: 'Order ECG and refer to cardiology today',
                    destinations: [{ condition: null, destinationId: 'good' }],
                    feedbackVariants: [{ id: 'vf_decision_1', condition: null, text: '' }] },
                { id: 'c_decision_2', label: 'Send home with painkillers and follow-up',
                    destinations: [{ condition: null, destinationId: 'mixed' }],
                    feedbackVariants: [{ id: 'vf_decision_2', condition: null, text: '' }] },
            ],
        },
        handover: {
            id: 'handover', title: 'Handover', type: 'outcome', x: 660, y: 460,
            objectiveTags: ['obj_safety'],
            variants: [{
                    id: 'v_handover_0', condition: null,
                    sceneText: "You hand over to the consultant. They take it from here. You used a senior's time but the patient is safe.",
                    media: [],
                }],
            choices: [],
            outcomeLabel: 'Safe outcome',
        },
        good: {
            id: 'good', title: 'Good outcome', type: 'outcome', x: 60, y: 660,
            objectiveTags: ['obj_safety', 'obj_communication'],
            variants: [{
                    id: 'v_good_0', condition: null,
                    sceneText: 'ECG shows ischemic changes. Cardiology accepts the same-day referral. The patient gets timely treatment and a good prognosis.',
                    media: [],
                }],
            choices: [],
            outcomeLabel: 'Best outcome',
        },
        mixed: {
            id: 'mixed', title: 'Adverse outcome', type: 'outcome', x: 380, y: 660,
            variants: [{
                    id: 'v_mixed_0', condition: null,
                    sceneText: 'The patient leaves dissatisfied. Two days later they re-present at A&E with a heart attack. Treatable, but the delay caused harm and a complaint.',
                    media: [],
                }],
            choices: [],
            outcomeLabel: 'Adverse outcome',
        },
    },
};
/**
 * A minimal, clean V6 scenario used as the seed for "New scenario" in the
 * editor (and the editor's pre-hydration placeholder). One start scene with a
 * single fallback variant and no choices yet — the lightest shape the validator
 * accepts without errors (a scene needs at least one fallback variant). No
 * objectives, variables, branding, intro, or closing, so the author starts from
 * a genuinely empty canvas rather than the demo.
 */
export const blankScenario = {
    formatVersion: 7,
    title: 'New scenario',
    startNodeId: 'start',
    variables: {},
    objectives: {},
    nodes: {
        start: {
            id: 'start',
            title: 'Start',
            type: 'start',
            x: 360,
            y: 80,
            variants: [{ id: 'v_start_0', condition: null, sceneText: '', media: [] }],
            choices: [],
        },
    },
};
//# sourceMappingURL=defaults.js.map